import React, { useState } from 'react';
import { X, Monitor, Smartphone, Gamepad2, Loader2, AlertCircle } from 'lucide-react';
import { NewDevice } from '../hooks/useDevices';

interface DeviceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (device: NewDevice) => Promise<{ success: boolean; error?: string }>;
  maxDevices: number;
  currentDeviceCount: number;
}

export const DeviceModal: React.FC<DeviceModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  maxDevices,
  currentDeviceCount
}) => {
  const [formData, setFormData] = useState<NewDevice>({
    name: '',
    type: 'pc',
    operating_system: '',
    child_name: '',
    child_age: 10
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deviceTypes = [
    { value: 'pc', label: 'Gaming PC', icon: Monitor },
    { value: 'console', label: 'Gaming Console', icon: Gamepad2 },
    { value: 'mobile', label: 'Mobile Device', icon: Smartphone }
  ] as const;

  const operatingSystems = {
    pc: ['Windows 11', 'Windows 10', 'macOS', 'Linux'],
    console: ['PlayStation 5', 'PlayStation 4', 'Xbox Series X/S', 'Xbox One', 'Nintendo Switch'],
    mobile: ['iOS', 'Android']
  };

  const handleInputChange = (field: keyof NewDevice, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError(null);
  };

  const handleTypeChange = (type: 'pc' | 'console' | 'mobile') => {
    setFormData(prev => ({
      ...prev,
      type,
      operating_system: '' // Reset OS when type changes
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      setError('Device name is required');
      return false;
    }
    if (formData.name.trim().length < 2) {
      setError('Device name must be at least 2 characters long');
      return false;
    }
    if (!formData.operating_system) {
      setError('Operating system is required');
      return false;
    }
    if (!formData.child_name.trim()) {
      setError('Child name is required');
      return false;
    }
    if (formData.child_name.trim().length < 2) {
      setError('Child name must be at least 2 characters long');
      return false;
    }
    if (formData.child_age < 6 || formData.child_age > 17) {
      setError('Child age must be between 6 and 17');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    if (currentDeviceCount >= maxDevices) {
      setError(`You can only add ${maxDevices} device${maxDevices > 1 ? 's' : ''} with your current plan`);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await onSubmit(formData);
      
      if (result.success) {
        // Reset form and close modal
        setFormData({
          name: '',
          type: 'pc',
          operating_system: '',
          child_name: '',
          child_age: 10
        });
        onClose();
      } else {
        setError(result.error || 'Failed to add device');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        name: '',
        type: 'pc',
        operating_system: '',
        child_name: '',
        child_age: 10
      });
      setError(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-xl font-bold text-gray-900">Add New Device</h3>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-red-700 font-medium">Error</p>
                <p className="text-sm text-red-600">{error}</p>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Device Type
            </label>
            <div className="grid grid-cols-3 gap-3">
              {deviceTypes.map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => handleTypeChange(value)}
                  className={`p-4 border-2 rounded-lg text-center transition-all ${
                    formData.type === value
                      ? 'border-teal-500 bg-teal-50 text-teal-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  disabled={isSubmitting}
                >
                  <Icon className="w-6 h-6 mx-auto mb-2" />
                  <span className="text-sm font-medium">{label}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Device Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="e.g., Emma's Gaming PC"
              required
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Operating System
            </label>
            <select
              value={formData.operating_system}
              onChange={(e) => handleInputChange('operating_system', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              required
              disabled={isSubmitting}
            >
              <option value="">Select operating system</option>
              {operatingSystems[formData.type].map((os) => (
                <option key={os} value={os}>{os}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Child's Name
            </label>
            <input
              type="text"
              value={formData.child_name}
              onChange={(e) => handleInputChange('child_name', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="Enter child's name"
              required
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Child's Age
            </label>
            <input
              type="number"
              min="6"
              max="17"
              value={formData.child_age}
              onChange={(e) => handleInputChange('child_age', parseInt(e.target.value) || 10)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              required
              disabled={isSubmitting}
            />
            <p className="text-xs text-gray-500 mt-1">Age must be between 6 and 17</p>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Adding...</span>
                </>
              ) : (
                <span>Add Device</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};